import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Copy } from 'lucide-react';
import {
  Button,
  DataTable,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  AlertMessage,
  PageHeader,
  SearchFilterBar,
  FilterTabs,
} from '../common/ui';
import Pagination from '../common/Pagination';
import { useAccountingActionBar } from './AccountingActionBar';
import LinkedFlag from './LinkedFlag';
import type { VascoInvoice, VascoInvoicesResponse, VascoStatsBucket, PaginationInfo, VascoLinkedFilter } from './types';
import { getWooCommerceOrderUrl } from './types';

// One tab per linkage leg, mirroring the ORDER and PAYMENT columns.
const LINKED_FILTERS: { key: VascoLinkedFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'order-linked', label: 'Order linked' },
  { key: 'order-not-linked', label: 'Order not linked' },
  { key: 'payment-linked', label: 'Payment linked' },
  { key: 'payment-not-linked', label: 'Payment not linked' },
];

const VascoInvoices: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<VascoInvoice[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 50, total: 0 });
  const [stats, setStats] = useState<{
    page: VascoStatsBucket;
    thisYear: VascoStatsBucket;
    allTime: VascoStatsBucket;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [linkedFilter, setLinkedFilter] = useState<VascoLinkedFilter>(
    (searchParams.get('linked') as VascoLinkedFilter) || 'all'
  );
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );

  const fetchInvoices = useCallback(async (page: number, search: string, linked: VascoLinkedFilter, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '50' };
      if (search) params.search = search;
      if (linked !== 'all') params.linked = linked;

      const { data } = await axios.get<VascoInvoicesResponse>('/api/accounting/vasco-invoices', { params, signal });
      if (signal?.aborted) return;
      setFetchError('');
      setInvoices(data.invoices);
      setPagination(data.pagination);
      if (data.stats) setStats(data.stats);
    } catch (err: any) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      setFetchError(err.response?.data?.error || 'Failed to load Vasco invoices');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchInvoices(currentPage, searchTerm, linkedFilter, controller.signal);
    return () => controller.abort();
  }, [fetchInvoices, currentPage, searchTerm, linkedFilter]);

  const refetchCurrentPage = () => fetchInvoices(currentPage, searchTerm, linkedFilter);

  const { actionButtons, actionAlerts } = useAccountingActionBar(refetchCurrentPage);

  const updateUrlParams = (linked: VascoLinkedFilter, search: string, page: number) => {
    const params = new URLSearchParams();
    if (linked && linked !== 'all') params.set('linked', linked);
    if (search && search.trim()) params.set('search', search.trim());
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  };

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchTerm(trimmed);
    setCurrentPage(1);
    updateUrlParams(linkedFilter, trimmed, 1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
    updateUrlParams(linkedFilter, '', 1);
  };

  const handleLinkedFilterChange = (filter: VascoLinkedFilter) => {
    setLinkedFilter(filter);
    setCurrentPage(1);
    updateUrlParams(filter, searchTerm, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrlParams(linkedFilter, searchTerm, page);
  };

  useEffect(() => {
    const linkedFromUrl = (searchParams.get('linked') as VascoLinkedFilter) || 'all';
    const searchFromUrl = searchParams.get('search') || '';
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    if (linkedFromUrl !== linkedFilter) setLinkedFilter(linkedFromUrl);
    if (searchFromUrl !== searchTerm) {
      setSearchInput(searchFromUrl);
      setSearchTerm(searchFromUrl);
    }
    if (pageFromUrl !== currentPage) setCurrentPage(pageFromUrl);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // PREJEMNIK (recipient name) is the human-readable line, KUPEC (buyer) is a
  // numeric customer code — show the name prominently, the code below it.
  const formatBuyer = (invoice: VascoInvoice) => {
    const buyer = invoice.buyer?.trim();
    const recipient = invoice.recipient?.trim();

    if (buyer && recipient && buyer !== recipient) {
      return (
        <div>
          <span className="text-sm font-medium block truncate max-w-[200px]" title={recipient}>
            {recipient}
          </span>
          <span className="text-xs text-gray-500 block truncate max-w-[200px]" title={buyer}>
            {buyer}
          </span>
        </div>
      );
    }

    const display = recipient || buyer || '—';
    return (
      <span className="text-sm block truncate max-w-[200px]" title={display}>
        {display}
      </span>
    );
  };

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Vasco Invoices"
        subtitle="Imported Vasco documents grouped by Vasco invoice"
        actions={actionButtons}
      />

      {actionAlerts}
      {fetchError && (
        <AlertMessage type="error" message={fetchError} title="" onDismiss={() => setFetchError('')} dismissible>
          {''}
        </AlertMessage>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <Button
          variant="secondary"
          onClick={() => navigate('/accounting')}
          icon={ArrowLeft}
        >
          Group by Bank transfer
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate('/accounting/b2b-orders')}
          icon={ArrowLeft}
        >
          Group by B2B order
        </Button>
      </div>

      <SearchFilterBar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onSearchSubmit={handleSearch}
        onSearch={handleSearch}
        onSearchClear={handleSearchClear}
        searchPlaceholder="Search by Vasco ID, order number, or buyer..."
        onFilterChange={() => {}}
      />

      <FilterTabs
        filters={LINKED_FILTERS}
        activeFilter={linkedFilter}
        onFilterChange={handleLinkedFilterChange}
      />

      <DataTable emptyState={null}>
        <TableHeader>
          <tr>
            <TableHeaderCell responsive={undefined}>Vasco ID</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Order #</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Buyer</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Amount</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Payment Method</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Order</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Payment</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message="Loading Vasco invoices…"
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : fetchError ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message="Unable to load Vasco invoices."
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : invoices.length === 0 ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message={
                    searchTerm
                      ? `No Vasco invoices match "${searchTerm}".`
                      : 'No Vasco invoices. Use Reconcile ▾ → Vasco to import a Vasco export.'
                  }
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : (
            invoices.map((invoice) => (
              <TableRow
                key={invoice.id}
                onClick={undefined}
                href={undefined}
                onKeyDown={undefined}
              >
                <TableCell responsive={undefined} className="whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium tabular-nums">{invoice.vascoInvoice}</span>
                    {invoice.isCreditNote && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-red-600 bg-red-50">
                        Credit note
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell responsive={undefined} className="whitespace-nowrap group">
                  {invoice.orderNumber ? (
                    <div className="flex items-center gap-2">
                      {invoice.wooOrderId != null ? (
                        <a
                          href={getWooCommerceOrderUrl(invoice.wooOrderId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          #{invoice.orderNumber}
                        </a>
                      ) : (
                        <span className="text-sm font-medium">#{invoice.orderNumber}</span>
                      )}
                      <button
                        type="button"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-600 focus:opacity-100 focus:outline-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(invoice.orderNumber || '');
                        }}
                        title="Copy order number"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell responsive={undefined}>
                  {formatBuyer(invoice)}
                </TableCell>
                <TableCell responsive={undefined}>
                  {invoice.amount != null ? (
                    <span
                      className={`font-medium tabular-nums ${invoice.amount < 0 ? 'text-red-600' : 'text-green-700'}`}
                    >
                      {invoice.amount.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell responsive={undefined}>
                  {invoice.paymentMethod ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      {invoice.paymentMethod}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell responsive={undefined}>
                  <LinkedFlag linked={invoice.linkedToOrder} title="Linked to order" />
                </TableCell>
                <TableCell responsive={undefined}>
                  <div className="flex items-center gap-1.5">
                    <LinkedFlag linked={invoice.linkedToPayment} title="Linked to payment" />
                    {(invoice.directPaymentCount ?? 0) > 0 && (
                      <span className="text-xs text-gray-400" title="Directly linked payments">
                        · {invoice.directPaymentCount} payment{invoice.directPaymentCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </DataTable>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        onPageChange={handlePageChange}
        onItemsPerPageChange={() => {}}
        showItemsPerPageSelector={false}
      />

      {/* Stats footer */}
      {stats && (
        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
          {([
            ['Current page', stats.page],
            ['This year', stats.thisYear],
            ['All time', stats.allTime],
          ] as [string, VascoStatsBucket][]).map(([label, s]) => (
            <div key={label} className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-400 font-medium mb-1.5">{label}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-600 font-medium">{s.total}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-green-700 bg-green-50">
                  {s.linkedOrder} order-linked
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-blue-700 bg-blue-50">
                  {s.linkedPayment} payment-linked
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VascoInvoices;
